import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { gableRoof } from '../roofSolids';
import {
  boxSolid,
  horizontalCap,
  orientedFace,
  planPoint,
  prismSolid,
  pyramidOverRing,
  ringPoints,
  subFrame,
  triangulateRing,
} from '../solids';
import { buildVessel, vesselOutline } from './churchVessel';
import { corners, type PlanRect } from './genericBlock';
import { transverseRoof } from './gothic/roofs';
import {
  buttress,
  centredWindow,
  edgePanel,
  gableWall,
  openedWall,
  pointedWindows,
  rectWall,
  type MasonryStyles,
} from './masonry';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout, type PlanFrame } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import readings from './bethanienPlan.json';

export const BETHANIEN = readings;
type Range = (typeof readings.ranges)[number];

export function bethanienFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Bethaniën lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

/** Collision uses the individual walls and buildings, leaving the private court accessible. */
export function bethanienObstacles(landmark: Landmark): Ring[] {
  const plan = bethanienFrame(landmark);
  return [
    corners(readings.chapel.nave),
    vesselOutline({ ...readings.chapel.choir, apse: 'polygonal' }),
    ...readings.ranges.map(corners),
  ].map((ring) => ring.map((p) => toLayout(plan, p)));
}

function turned(r: PlanRect): PlanRect {
  return { u0: r.v0, u1: r.v1, v0: -r.u1, v1: -r.u0 };
}

function roofWithGables(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, r: Range): void {
  const vc = (r.v0 + r.v1) / 2;
  const holes = readings.roofGables.map((u) =>
    corners({ u0: u - 0.75, u1: u + 0.75, v0: 9.7, v1: 11.7 })
  );
  for (const sign of [-1, 1]) {
    const edge = sign < 0 ? r.v0 : r.v1;
    const slope = corners({ u0: r.u0, u1: r.u1, v0: Math.min(edge, vc), v1: Math.max(edge, vc) });
    for (const tri of triangulateRing(slope, sign < 0 ? holes : []))
      orientedFace(
        builder,
        styles.roof,
        frame,
        tri.map(([u, v]) =>
          planPoint(u, r.eaves + ((r.ridge - r.eaves) * (v - edge)) / (vc - edge), v)
        ),
        [0, 1, -sign]
      );
  }
  const roofAt = (v: number) => r.eaves + ((r.ridge - r.eaves) * (v - r.v0)) / (vc - r.v0);
  for (const u of readings.roofGables) {
    const u0 = u - 0.75,
      u1 = u + 0.75,
      v0 = 9.7,
      v1 = 11.7;
    for (const back of [false, true]) {
      const v = back ? v1 : v0;
      const edge: readonly [Vec2, Vec2] = back
        ? [
            [u1, v],
            [u0, v],
          ]
        : [
            [u0, v],
            [u1, v],
          ];
      openedWall(
        builder,
        styles,
        frame,
        edgePanel(...edge),
        [
          [0, roofAt(v)],
          [1.5, roofAt(v)],
          [1.5, 9.3],
          [0.75, 10.4],
          [0, 9.3],
        ],
        back
          ? []
          : [
              [
                [0.5, 7.35],
                [1, 7.35],
                [1, 8.5],
                [0.5, 8.5],
              ],
            ]
      );
    }
    for (const sign of [-1, 1]) {
      const x = sign < 0 ? u0 : u1;
      orientedFace(
        builder,
        styles.wall,
        frame,
        [
          planPoint(x, roofAt(v0), v0),
          planPoint(x, roofAt(v1), v1),
          planPoint(x, 9.3, v1),
          planPoint(x, 9.3, v0),
        ],
        [sign, 0, 0]
      );
    }
    transverseRoof(builder, styles, frame, { u0, u1, v0, v1 }, { eaveY: 9.3, ridgeY: 10.4 });
  }
}

function range(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, r: Range): void {
  const f = r.alongU ? frame : { ...frame, yaw: frame.yaw + Math.PI / 2 };
  const rect = r.alongU ? r : turned(r),
    ring = corners(rect);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const end = side % 2 === 1;
    const bays = Math.max(1, Math.floor(length / 3.8));
    const houseFront = r.id.startsWith('achterburgwal-front-') && side === 3;
    const touching = readings.ranges
      .filter((other) => other !== r)
      .flatMap((other) => {
        const q = r.alongU ? other : { ...turned(other), eaves: other.eaves };
        let span: [number, number] | null = null;
        if (side === 0 && q.v1 === rect.v0)
          span = [Math.max(q.u0, rect.u0) - rect.u0, Math.min(q.u1, rect.u1) - rect.u0];
        if (side === 2 && q.v0 === rect.v1)
          span = [rect.u1 - Math.min(q.u1, rect.u1), rect.u1 - Math.max(q.u0, rect.u0)];
        if (side === 1 && q.u0 === rect.u1)
          span = [Math.max(q.v0, rect.v0) - rect.v0, Math.min(q.v1, rect.v1) - rect.v0];
        if (side === 3 && q.u1 === rect.u0)
          span = [rect.v1 - Math.min(q.v1, rect.v1), rect.v1 - Math.max(q.v0, rect.v0)];
        return span && span[1] > span[0] ? [{ x0: span[0], x1: span[1], height: q.eaves }] : [];
      });
    const holes: Ring[] = [];
    if (houseFront) {
      const door = length * 0.28;
      holes.push([
        [door - 0.43, 0.1],
        [door + 0.43, 0.1],
        [door + 0.43, 2.45],
        [door - 0.43, 2.45],
      ]);
      for (let floor = 0; floor < r.floors; floor++)
        for (const share of floor === 0 ? [0.72] : [0.28, 0.72]) {
          const x = length * share,
            y = 1.15 + floor * 2.7;
          holes.push([
            [x - 0.36, y],
            [x + 0.36, y],
            [x + 0.36, y + 1.5],
            [x - 0.36, y + 1.5],
          ]);
        }
    } else if (side !== r.blindSide)
      for (let floor = 0; floor < r.floors; floor++)
        for (let bay = 0; bay < bays; bay++) {
          const x = ((bay + 0.5) * length) / bays,
            y = 1.05 + floor * 2.7;
          if (touching.some((t) => x - 0.46 < t.x1 && x + 0.46 > t.x0 && y < t.height)) continue;
          holes.push([
            [x - 0.4, y],
            [x + 0.4, y],
            [x + 0.4, y + 1.5],
            [x - 0.4, y + 1.5],
          ]);
        }
    rectWall(builder, styles, f, [from, to], [0, r.eaves], holes);
    if (end)
      openedWall(
        builder,
        styles,
        f,
        edgePanel(from, to),
        [
          [0, r.eaves],
          [length, r.eaves],
          [length / 2, r.ridge],
        ],
        [
          [
            [length / 2 - 0.22, r.eaves + 0.35],
            [length / 2 + 0.22, r.eaves + 0.35],
            [length / 2 + 0.22, r.eaves + 1],
            [length / 2 - 0.22, r.eaves + 1],
          ],
        ]
      );
    const face = subFrame(f, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
    if (houseFront) {
      const x = length * 0.28;
      boxSolid(
        builder,
        { ...styles.wall, color: styles.opening.color, surface: SURFACE.wood },
        face,
        [x - 0.42, 0.1, -0.27],
        [x + 0.42, 2.44, -0.21],
        { top: true, bottom: true }
      );
    }
    for (const h of houseFront ? holes.slice(1) : holes) {
      const x = (h[0]![0] + h[1]![0]) / 2,
        y = h[0]![1];
      boxSolid(
        builder,
        styles.glazing!.reveal,
        face,
        [x - 0.46, y - 0.08, -0.08],
        [x + 0.46, y + 0.03, 0.01],
        { top: true, bottom: true }
      );
      boxSolid(
        builder,
        styles.glazing!.reveal,
        face,
        [x - 0.025, y, -0.025],
        [x + 0.025, y + 1.5, 0.025],
        { top: true, bottom: true }
      );
    }
  });
  horizontalCap(builder, styles.wall, f, ring, 0.01, true);
  if (r.id === 'barndesteeg-refter') roofWithGables(builder, f, styles, r);
  else
    gableRoof(builder, styles, f, {
      ...rect,
      eaveY: r.eaves,
      ridgeY: r.ridge,
      ends: { start: false, end: false },
    });
}

function chapel(builder: MeshBuilder, frame: Frame, styles: MasonryStyles): void {
  const n = readings.chapel.nave,
    ring = corners(n);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    if (side % 2 === 0) {
      const windows = [
        ...pointedWindows(length, length / n.bays, 1.55, 4.8),
        ...pointedWindows(length, length / n.bays, 6.3, 10.3),
      ];
      rectWall(builder, styles, frame, [from, to], [0, n.eaves], windows);
      for (let i = 0; i <= n.bays; i++) {
        const u = n.u0 + ((n.u1 - n.u0) * i) / n.bays,
          v = side === 0 ? n.v0 : n.v1;
        buttress(builder, styles, frame, [u, v], [0, side === 0 ? -1 : 1], {
          width: 0.5,
          depth: 0.45,
          height: 10.8,
          pinnacle: 0,
        });
      }
    } else
      gableWall(
        builder,
        styles,
        frame,
        [from, to],
        { eaveY: n.eaves, apexY: n.ridge },
        side === 3 ? centredWindow(length, 5.8, 10.3, 0.22) : []
      );
  });
  gableRoof(builder, styles, frame, {
    ...n,
    eaveY: n.eaves,
    ridgeY: n.ridge,
    ends: { start: false, end: false },
  });
  buildVessel(
    builder,
    styles,
    frame,
    { ...readings.chapel.choir, apse: 'polygonal' },
    { longWindows: { sill: 2.1, top: 6.8 }, westWindow: false, bay: 4 }
  );
  // Small choir roof turret is depicted; its dimensions are estimated.
  const turret = ringPoints([5, -13.3], 0.48, 8, Math.PI / 8);
  turret.forEach((from, i) => {
    const to = turret[(i + 1) % turret.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [11.9, 14.2],
      [
        [
          [width * 0.25, 12.5],
          [width * 0.75, 12.5],
          [width * 0.75, 13.9],
          [width * 0.25, 13.9],
        ],
      ],
      0
    );
  });
  pyramidOverRing(builder, styles, frame, turret, 14.2, { plan: [5, -13.3], y: 16.7 });
}

/** Period-image masses and dated medieval wings; the court is not a standard four-range cloister. */
export function buildBethanienConvent(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY
): void {
  const frame = meshFrameOf(bethanienFrame(landmark));
  const base = paintedChapelStyles(colors, ids, tracery);
  const styles = { ...base, wall: { ...base.wall, surface: SURFACE.brick } };
  chapel(builder, frame, styles);
  for (const r of readings.ranges)
    range(
      builder,
      frame,
      r.roof === 'slate'
        ? styles
        : { ...styles, roof: { ...styles.roof, color: colors.tile, surface: SURFACE.roofTile } },
      r
    );
  const e = readings.entry;
  prismSolid(builder, styles.wall, frame, corners({ ...e, u1: e.u0 + 0.5 }), 2.7, 3.4, {
    top: true,
    bottom: true,
  });
  const ground = { ...styles.wall, color: colors.ground, surface: SURFACE.street };
  horizontalCap(builder, ground, frame, corners(e), 0.03, true);
  horizontalCap(builder, ground, frame, corners(readings.court), 0.03, true);
  const g = readings.garden;
  for (let column = 0; column < g.columns; column++)
    for (let row = 0; row < g.rows; row++) {
      const du = (g.u1 - g.u0) / g.columns,
        dv = (g.v1 - g.v0) / g.rows;
      horizontalCap(
        builder,
        { ...ground, color: colors.garden, surface: SURFACE.garden },
        frame,
        corners({
          u0: g.u0 + column * du + 0.45,
          u1: g.u0 + (column + 1) * du - 0.45,
          v0: g.v0 + row * dv + 0.45,
          v1: g.v0 + (row + 1) * dv - 0.45,
        }),
        0.035,
        true
      );
    }
  horizontalCap(builder, ground, frame, corners({ ...e, u1: g.u0 - 0.5 }), 0.045, true);
}
